// React
import { useCallback, useMemo } from 'react';

// Internal
import { useOrganizations } from '../contexts/OrganizationsContext';

// Types
import { BookkeepingPermission, type PermissionTypes } from '@/api/__types/roles.types';
import { useOrgMember } from '@/api/organization';
import { useEnabledFeatures } from '@/contexts/EnabledFeatures';

export type CheckPermissionFunction = (permission?: PermissionTypes) => boolean;

const usePermission = (
  permission?: PermissionTypes,
): {
  hasPermission: boolean | undefined;
  checkPermission: CheckPermissionFunction;
} => {
  const { organization } = useOrganizations();
  const { data: memberRoleAndPermission } = useOrgMember(organization);
  const { isHideBookkeepingEnabled } = useEnabledFeatures();

  const bookkeepingPermissions = useMemo(() => Object.values(BookkeepingPermission), []);

  // Process member role and permissions based on feature flags
  const processedMemberRoleAndPermission = useMemo(() => {
    if (isHideBookkeepingEnabled && memberRoleAndPermission) {
      // Filter out all BookkeepingPermissions if Bookkeeping is hidden by a feature flag
      const filteredPermissions = memberRoleAndPermission.permissions.filter(
        (permission) =>
          !bookkeepingPermissions.includes(permission as unknown as BookkeepingPermission),
      );

      return {
        ...memberRoleAndPermission,
        permissions: filteredPermissions,
      };
    }

    return memberRoleAndPermission;
  }, [isHideBookkeepingEnabled, memberRoleAndPermission, bookkeepingPermissions]);

  const checkPermission = useCallback(
    (permission?: string) => {
      if (permission === undefined) return true;
      return !!processedMemberRoleAndPermission?.permissions.some((p) => p === permission);
    },
    [processedMemberRoleAndPermission],
  );

  const hasPermission = useMemo(() => {
    if (!permission) return true;
    if (!processedMemberRoleAndPermission) return undefined;
    return checkPermission(permission);
  }, [permission, processedMemberRoleAndPermission, checkPermission]);

  return { hasPermission, checkPermission };
};

export default usePermission;
